/**
 * React Hook for RTDB-cached NFL standings with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import { rtdbNFLService } from '../rtdbNFLService';
import nflRTDBCache from '../rtdbCache';
import { CACHE_PATHS, logCache } from '../rtdbConfig';

/**
 * Hook for fetching NFL standings with RTDB caching and real-time updates
 * @param {Object} params - Query parameters
 * @param {number} params.league - League ID (default: 1 for NFL)
 * @param {number} params.season - Season year
 * @param {number} params.team - Team ID for filtering (optional)
 * @param {string} params.group - Group/Conference filter (optional)
 * @param {boolean} params.preseason - Include preseason standings
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Standings data, loading state, error, and refetch function
 */
export const useRTDBStandings = ({ 
  league = 1,
  season = 2025,
  team,
  group,
  preseason = false,
  enabled = true,
  realtime = true 
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchStandings = useCallback(async () => {
    if (!enabled) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');
      
      logCache('info', 'useRTDBStandings fetching', { league, season, team, group, preseason });
      
      const response = await rtdbNFLService.getStandings(
        league,
        season,
        team,
        group,
        preseason
      );
      
      const standings = response?.response || [];
      setData(standings);
      setCacheStatus('success');
      
      logCache('info', `useRTDBStandings loaded ${standings.length} standings entries`);
    } catch (err) {
      setError(err.message || 'Failed to load standings');
      setCacheStatus('error');
      console.error('Failed to load NFL standings:', err);
    } finally {
      setLoading(false);
    }
  }, [league, season, team, group, preseason, enabled]);

  // Set up real-time subscription
  useEffect(() => {
    if (!enabled || !realtime) return;

    let unsubscribe = () => {};
    
    // Build cache path
    const cacheKey = `standings_${league || 1}_${season || 2025}_${group || 'all'}`;
    const cachePath = `${CACHE_PATHS.STANDINGS}/${cacheKey}`;

    // Subscribe to real-time updates
    unsubscribe = nflRTDBCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        logCache('info', 'Real-time standings update received', { path: cachePath });
        
        const standings = cachedData.response || cachedData || [];
        setData(standings);
        setCacheStatus('realtime-update');
      } else {
        // Cache expired or missing, trigger refresh
        fetchStandings();
      }
    });

    // Initial fetch
    fetchStandings();

    // Refresh standings periodically (every 6 hours is reasonable for standings)
    const interval = setInterval(fetchStandings, 6 * 60 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [league, season, team, group, preseason, enabled, realtime]); // fetchStandings excluded

  // Computed values for easy access based on actual API structure
  const conferenceStandings = useCallback(() => {
    if (!data || data.length === 0) return { AFC: [], NFC: [] };
    
    const afc = data.filter(standing => 
      standing.conference === 'American Football Conference' || 
      standing.conference === 'AFC'
    );
    const nfc = data.filter(standing => 
      standing.conference === 'National Football Conference' || 
      standing.conference === 'NFC'
    );
    
    return { AFC: afc, NFC: nfc };
  }, [data]);

  const divisionStandings = useCallback(() => {
    if (!data || data.length === 0) return {};
    
    const divisions = {};
    data.forEach(standing => {
      const division = standing.division || 'Unknown';
      if (!divisions[division]) {
        divisions[division] = [];
      }
      divisions[division].push(standing);
    });
    
    // Sort each division by position
    Object.keys(divisions).forEach(division => {
      divisions[division].sort((a, b) => a.position - b.position);
    });
    
    return divisions;
  }, [data]);

  const getTeamStanding = useCallback((teamId) => {
    return data.find(standing => standing.team?.id === teamId);
  }, [data]);

  // Get win percentage for a team
  const getWinPercentage = useCallback((standing) => {
    if (!standing) return 0;
    const totalGames = standing.won + standing.lost + (standing.ties || 0);
    if (totalGames === 0) return 0;
    return ((standing.won + (standing.ties || 0) * 0.5) / totalGames).toFixed(3);
  }, []);

  // Get playoff teams (top 7 from each conference)
  const getPlayoffTeams = useCallback(() => {
    const { AFC, NFC } = conferenceStandings();
    
    // Sort by win percentage and get top 7
    const afcPlayoffs = AFC.sort((a, b) => {
      const aWinPct = getWinPercentage(a);
      const bWinPct = getWinPercentage(b);
      return bWinPct - aWinPct;
    }).slice(0, 7);
    
    const nfcPlayoffs = NFC.sort((a, b) => {
      const aWinPct = getWinPercentage(a);
      const bWinPct = getWinPercentage(b);
      return bWinPct - aWinPct;
    }).slice(0, 7);
    
    return { AFC: afcPlayoffs, NFC: nfcPlayoffs };
  }, [data, conferenceStandings, getWinPercentage]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchStandings,
    cacheStatus,
    isConnected: nflRTDBCache.isConnected(),
    // Computed helpers
    conferenceStandings: conferenceStandings(),
    divisionStandings: divisionStandings(),
    getTeamStanding,
    getWinPercentage,
    getPlayoffTeams: getPlayoffTeams()
  };
};

/**
 * Hook for conference-specific standings
 */
export const useRTDBConferenceStandings = ({ 
  conference = 'AFC',
  season = 2025,
  enabled = true 
}) => {
  const { data, loading, error, refetch } = useRTDBStandings({
    league: 1,
    season,
    group: conference,
    enabled
  });

  // Filter by conference
  const conferenceData = data.filter(standing => {
    const conf = standing.conference;
    if (conference === 'AFC') {
      return conf === 'American Football Conference' || conf === 'AFC';
    } else if (conference === 'NFC') {
      return conf === 'National Football Conference' || conf === 'NFC';
    }
    return false;
  });

  return { 
    data: conferenceData, 
    loading, 
    error, 
    refetch 
  };
};

/**
 * Hook for division-specific standings
 */
export const useRTDBDivisionStandings = ({ 
  division,
  season = 2025,
  enabled = true 
}) => {
  const { data, loading, error, refetch } = useRTDBStandings({
    league: 1,
    season,
    enabled
  });

  // Filter by division
  const divisionData = data.filter(standing => 
    standing.division === division
  );

  // Sort by position within division
  divisionData.sort((a, b) => a.position - b.position);

  return { 
    data: divisionData, 
    loading, 
    error, 
    refetch 
  };
};